import React, { Component } from "react";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Alert,
} from "reactstrap";
import triggerOnPremRemoteCommand from "../services/remoteControlService";
import DisplayWhen from "../components/common/base/DisplayWhen";
import SelectCommand from "../components/remotecontrol/SelectCommand";
import Command from "../components/remotecontrol/Command";
import SelectServer from "../components/remotecontrol/SelectServer";
import GenerateNucImageModal from "../components/remotecontrol/GenerateNucImageModal";
import { getServerById } from "../services/serversService";
import getStoreCaptureDeviceRecorders from "../services/captureDeviceRecorderService";
import { getStore } from "../services/storeService";
import getCommands from "../services/commandsService";
import userHasRole from "../components/shared/utils/authUtils";
import { resellerRole } from "../components/shared/constants";

/** Commands whose forms are prefilled from the store's capture devices. */
const DVR_COMMANDS = ["screenshot", "connect-dvr-interface", "update-dvr"];

/**
 * Narrow a store's recorders to the ones the selected server can actually reach.
 *
 * Both recorder sources are store-scoped, not machine-scoped: legacy `VideoRecorder` rows hang
 * off the store (there is no machine foreign key, hence the `store_videorecorders` endpoint), and
 * the CaptureDevice tree is fetched per store. A store can spread its cameras over several
 * inference machines, and running a DVR command against a device that belongs to another machine
 * would push that device's address and credentials through the wrong server. The per-channel
 * inference machine is the only thing tying a recorder to a machine, and both shapes carry it.
 *
 * A device whose cameras span several machines legitimately belongs to each of them, so matching
 * on "any channel on this host" is what scopes correctly. There is no fallback to the unscoped
 * list: a recorder is included only when a channel confirms it runs on this server, so an unknown
 * hostname — which confirms nothing — yields nothing. Returning empty costs the operator only the
 * prefill, since the view already renders a blank, fully usable form when there are no recorders,
 * whereas an unscoped list would hand them another machine's credentials to send.
 *
 * @param {Array<object>} recorders - Recorders in the legacy shape, from either source.
 * @param {string|undefined} hostname - The selected server's hostname.
 * @returns {Array<object>} Only the recorders confirmed to run on that server.
 */
export function scopeRecordersToServer(recorders, hostname) {
  if (!hostname || !Array.isArray(recorders)) return [];
  return recorders.filter((recorder) =>
    (recorder?.channels || []).some((channel) => channel?.inference_machine_data?.hostname === hostname)
  );
}

// Extract the shareable result (download link + SHA256) from the build stdout.
function parseImageResult(stdout) {
  if (!stdout) return null;
  const url = stdout.match(/^Download link:\s*(\S+)/m)?.[1] || null;
  const sha256 = stdout.match(/^SHA256:\s*([0-9a-fA-F]+)/m)?.[1] || null;
  return url || sha256 ? { url, sha256 } : null;
}

class RemoteControlView extends Component {
  // Read once, at construction: cleanUrlServerId drops the whole query string as soon as the
  // reachability ping comes back, so a render-time read would report a deep link as store-less.
  urlStoreId = new URLSearchParams(window.location.search).get("store");

  state = {
    selectedServer: null,
    enableDropdown: false,
    selectedCommand: null,
    videoRecorders: [],
    activeTabIndex: 0,
    isDisabledButton: true,
    running: false,
    listServersOutput: null,
    generateImageOutput: null,
    generateImageError: null,
    generateImageResult: null,
    isGenerateImageModalOpen: false,
    serverType: null,
  };

  async componentDidMount() {
    const urlServerId = this.getUrlServerId();
    if (urlServerId) {
      const [{ data: selectedServer }, commands, storeRecorders] = await Promise.all([
        this.fetchServerById(urlServerId),
        this.fetchCommands(),
        this.fetchVideoRecorders(),
      ]);
      const selectedCommand = commands.find((item) => item.label === this.getUrlServerCommand());
      this.setState({
        selectedServer,
        enableDropdown: false,
        selectedCommand,
        videoRecorders: scopeRecordersToServer(storeRecorders, selectedServer?.hostname),
        // The submit buttons start disabled and are only released by handleReachServer, i.e. once
        // SelectServer's reachability ping comes back. On a deep link the server is already
        // resolved, so gating the form on that probe only buys a window where the button silently
        // swallows clicks — and the probe polls while the box answers "running", with no cap, so
        // that window can last indefinitely. The gate protects nothing anyway: handleReachServer
        // releases the button even when the ping failed. An unreachable server now surfaces as the
        // command's own error instead of an inert button.
        isDisabledButton: false,
      });
    }
  }

  fetchServerById = async (urlServerId) => {
    const server = await getServerById(urlServerId);
    return server;
  };

  fetchCommands = async () => {
    const commands = await getCommands();
    return commands;
  };

  // Resolve the recorders whose credentials prefill the DVR-facing command forms. The
  // camera-model source is keyed on the store, so it needs the `store` query param the
  // entry points add; without it the forms render unprefilled. Always resolves to an
  // array — an `undefined` here renders no form at all, since the render branches test
  // `videoRecorders.length`.
  fetchVideoRecorders = async () => {
    const { urlStoreId } = this;
    if (!urlStoreId) {
      return [];
    }
    try {
      const { data: store } = await getStore(urlStoreId);
      return await getStoreCaptureDeviceRecorders(urlStoreId, {
        localInference: !!store?.local_inference,
      });
    } catch (ex) {
      // A failed camera-model lookup must not cost the operator the whole page: the forms
      // stay usable unprefilled, which is what a store with no recorders already renders.
      console.error(`Failed to fetch capture devices for store ${urlStoreId}:`, ex);
      return [];
    }
  };

  getUrlServerId = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("server");
  };

  getUrlServerCommand = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("command");
  };

  cleanUrlServerId = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("server");
    const newUrl = window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  };

  toggleTab = (tabIndex) => {
    const { activeTabIndex } = this.state;
    if (activeTabIndex !== tabIndex) {
      this.setState({
        activeTabIndex: tabIndex,
      });
    }
  };

  handleReachServer = (server, reached, serverType = null) => {
    const { serverType: currentServerType } = this.state;
    const serverTypeChanged = currentServerType && currentServerType !== serverType;

    this.setState({
      selectedServer: server,
      enableDropdown: reached,
      serverType,
      ...(this.getUrlServerId() ? {} : { selectedCommand: null }),
      ...(this.getUrlServerId() ? {} : { videoRecorders: [] }),
      // Clear the command when switching between on-prem and cloud
      ...(serverTypeChanged ? { selectedCommand: null } : {}),
      isDisabledButton: false,
    });
    this.cleanUrlServerId();
  };

  handleSelectCommand = (command) => {
    this.setState({ selectedCommand: command });
  };

  handleRunCommand = (running) => {
    this.setState({ enableDropdown: !running });
  };

  handleListServersReverseTunnel = async () => {
    const { running } = this.state;

    if (running) {
      toast.warn("Command list servers reverse tunnel is already running");
      return;
    }

    const data = {
      hostname: "taxi",
      cmd: "list-servers-reverse-tunnel",
      cmdArgs: [],
    };

    this.setState({
      selectedCommand: { label: "list-servers-reverse-tunnel" },
    });

    this.handleRunCommand(true);
    this.setState({
      running: true,
      listServersOutput: null,
    });
    toast.info("Running list-servers-reverse-tunnel command, please wait...");
    await triggerOnPremRemoteCommand(data)
      .then((response) => {
        if (response.status === "OK") {
          toast.success("Command succeded !");
        } else if (response.status === "FAILED") {
          toast.error("Command failed...");
        }
        this.handleRunCommand(false);
        this.setState({
          listServersOutput: response.stdout,
          running: false,
        });
      })
      .catch(() => {
        this.handleRunCommand(false);
        this.setState({
          running: false,
        });
        toast.error("Unhandled error");
      });
  };

  toggleGenerateImageModal = () => {
    const { isGenerateImageModalOpen } = this.state;
    this.setState({ isGenerateImageModalOpen: !isGenerateImageModalOpen });
  };

  handleGenerateImageForNuc = async (format, group) => {
    const { running } = this.state;

    if (running) {
      toast.warn("A command is already running");
      return;
    }

    const data = {
      hostname: "taxi",
      cmd: "generate-disk-image",
      cmdArgs: { format, group },
    };

    this.setState({
      selectedCommand: { label: "generate-disk-image" },
    });

    this.handleRunCommand(true);
    this.setState({
      running: true,
      generateImageOutput: null,
      generateImageError: null,
      generateImageResult: null,
    });
    toast.info(`Generating ${format} image for group ${group}, please wait...`);
    // The image build takes minutes; poll every 5s and stream the partial
    // output as it comes in.
    await triggerOnPremRemoteCommand(data, {
      pollIntervalMs: 5000,
      onProgress: (partial) => {
        if (partial && partial.stdout) {
          this.setState({ generateImageOutput: partial.stdout });
        }
      },
    })
      .then((response) => {
        if (response.status === "OK") {
          toast.success("Image generation succeeded !");
        } else if (response.status === "FAILED") {
          toast.error("Image generation failed...");
        }
        this.handleRunCommand(false);
        this.setState({
          generateImageOutput: response.stdout || null,
          // Keep the error visible (in red) after the output: the command is
          // long, so a transient toast is easy to miss.
          generateImageError: response.stderr || null,
          // Highlight the shareable result (download link + checksum).
          generateImageResult: response.status === "OK" ? parseImageResult(response.stdout) : null,
          running: false,
        });
      })
      .catch((ex) => {
        this.handleRunCommand(false);
        this.setState({
          running: false,
        });
        if (ex.response && ex.response.status === 403) {
          toast.error("You are not allowed to generate a NUC image (veesion group membership required)");
        } else {
          toast.error("Unhandled error");
        }
      });
  };

  render() {
    const {
      selectedServer,
      enableDropdown,
      selectedCommand,
      videoRecorders,
      activeTabIndex,
      isDisabledButton,
      listServersOutput,
      generateImageOutput,
      generateImageError,
      generateImageResult,
      isGenerateImageModalOpen,
      running,
      serverType,
    } = this.state;
    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <CardTitle tag="h4">Select an on premise server</CardTitle>
                <CardSubtitle className="card-category" style={{ textTransform: "none" }}>
                  You can only select existing on premise servers
                </CardSubtitle>
                <DisplayWhen condition={!this.getUrlServerId() || selectedServer}>
                  <SelectServer
                    handleListServersReverseTunnel={this.handleListServersReverseTunnel}
                    handleGenerateImageForNuc={this.toggleGenerateImageModal}
                    handleReachServer={(server, reached) => this.handleReachServer(server, reached, "onprem")}
                    server={selectedServer}
                    mode="onprem"
                  />
                </DisplayWhen>
                <DisplayWhen condition={!userHasRole(resellerRole)}>
                  <hr />
                  <CardTitle tag="h4">Select a cloud server</CardTitle>
                  <CardSubtitle className="card-category" style={{ textTransform: "none" }}>
                    You can only select existing cloud servers
                  </CardSubtitle>
                  <DisplayWhen condition={!this.getUrlServerId() || selectedServer}>
                    <SelectServer
                      handleReachServer={(server, reached) => this.handleReachServer(server, reached, "cloud")}
                      server={selectedServer}
                      mode="cloud"
                    />
                  </DisplayWhen>
                </DisplayWhen>
                {selectedServer && (
                  <SelectCommand
                    enabled={enableDropdown}
                    handleSelectCommand={this.handleSelectCommand}
                    selectedCommandLabel={selectedCommand}
                    serverType={serverType}
                  />
                )}
                {selectedCommand &&
                  DVR_COMMANDS.includes(selectedCommand.label) &&
                  !this.urlStoreId &&
                  videoRecorders.length === 0 && (
                    <Alert color="info" className="mt-3">
                      No store in the URL, so the DVR fields are not prefilled. Reach this page from the store to get
                      them filled in.
                    </Alert>
                  )}
                {selectedCommand &&
                  (!DVR_COMMANDS.includes(selectedCommand.label) || videoRecorders.length === 0) &&
                  selectedCommand.label !== "list-servers-reverse-tunnel" &&
                  selectedCommand.label !== "generate-disk-image" && (
                    <Command
                      server={selectedServer}
                      command={selectedCommand}
                      handleRunCommand={this.handleRunCommand}
                      videoRecorder={null}
                      isDisabledButton={isDisabledButton}
                    />
                  )}
                {selectedCommand && DVR_COMMANDS.includes(selectedCommand.label) && videoRecorders.length > 0 && (
                  <Nav tabs>
                    {videoRecorders.map((videoRecorder, index) => (
                      <NavItem role="button">
                        <NavLink
                          className={activeTabIndex === index ? "active" : ""}
                          onClick={() => {
                            this.toggleTab(index);
                          }}
                        >
                          <p>
                            DVR {index + 1} : {videoRecorder.video_recorder_type_name}
                          </p>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                )}
                {selectedCommand && DVR_COMMANDS.includes(selectedCommand.label) && videoRecorders.length > 0 && (
                  <TabContent activeTab={activeTabIndex}>
                    {videoRecorders.map((videoRecorder, index) => (
                      <TabPane tabId={index}>
                        <Command
                          server={selectedServer}
                          command={selectedCommand}
                          handleRunCommand={this.handleRunCommand}
                          videoRecorder={videoRecorder}
                          isDisabledButton={isDisabledButton}
                        />
                      </TabPane>
                    ))}
                  </TabContent>
                )}
                {running && <Spinner color="primary" />}
                {selectedCommand && selectedCommand.label === "list-servers-reverse-tunnel" && listServersOutput && (
                  <div>
                    <h5>List servers connected to the reverse tunnel:</h5>
                    <pre>{listServersOutput}</pre>
                  </div>
                )}
                {selectedCommand &&
                  selectedCommand.label === "generate-disk-image" &&
                  (generateImageOutput || generateImageError || generateImageResult) && (
                    <div>
                      <h5>Generate image for NUC output:</h5>
                      {generateImageResult && (
                        <Alert color="success">
                          <h6>Image ready — share this:</h6>
                          {generateImageResult.url && (
                            <div className="text-break">
                              <strong>Download link:</strong>{" "}
                              <a href={generateImageResult.url} target="_blank" rel="noopener noreferrer">
                                {generateImageResult.url}
                              </a>
                            </div>
                          )}
                          {generateImageResult.sha256 && (
                            <div className="text-break">
                              <strong>SHA256:</strong> <code>{generateImageResult.sha256}</code>
                            </div>
                          )}
                        </Alert>
                      )}
                      {generateImageOutput && <pre>{generateImageOutput}</pre>}
                      {generateImageError && (
                        <pre style={{ color: "red", whiteSpace: "pre-wrap" }}>{generateImageError}</pre>
                      )}
                    </div>
                  )}
                <GenerateNucImageModal
                  isOpen={isGenerateImageModalOpen}
                  onClose={this.toggleGenerateImageModal}
                  onSubmit={this.handleGenerateImageForNuc}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default RemoteControlView;
